/**
 * driverLevel — utilitaire pour normaliser le niveau d'un chauffeur.
 *
 * Cible le bug user 17/06/2026 : les chauffeurs rachetés étaient générés
 * avec `level: 'Junior' | 'Confirmé' | 'Senior'` (string) alors que le reste
 * du système utilise un entier 1-5 (cf. LEVEL_BONUSES dans
 * data/impactTrees.js). Cette discordance provoquait :
 *   - "Niveau moyen NaN" sur le dashboard RH
 *   - "1495 / NaN XP" sur la barre d'XP
 *   - Formation silencieusement bloquée (UI gating cassé)
 *
 * Mapping vers le 1-5 numérique :
 *   - 'Junior' / 'junior' / 'Débutant'      → 1
 *   - 'Junior+'                              → 2
 *   - 'Confirmé' / 'confirmed' / 'Expérimenté' → 3
 *   - 'Senior' / 'Sénior' / 'senior'        → 4
 *   - 'Expert'                               → 5
 *
 * Si la valeur est déjà numérique entre 1-5 → renvoyée telle quelle.
 * Si invalide / undefined → 1 par défaut.
 */
const STRING_TO_NUMERIC_LEVEL = {
  junior: 1,
  débutant: 1,
  debutant: 1,
  'junior+': 2,
  confirmé: 3,
  confirme: 3,
  confirmed: 3,
  expérimenté: 3,
  experimente: 3,
  experienced: 3,
  senior: 4,
  sénior: 4,
  expert: 5,
};

export function normalizeDriverLevel(level) {
  if (typeof level === 'number' && level >= 1 && level <= 5) {
    return Math.round(level);
  }
  if (typeof level === 'string') {
    const key = level.trim().toLowerCase();
    const mapped = STRING_TO_NUMERIC_LEVEL[key];
    if (mapped) return mapped;
  }
  return 1;
}

/**
 * Migration helper — applique `normalizeDriverLevel` à un tableau de
 * chauffeurs (mutation immuable). Utilisé au chargement d'une partie
 * sauvegardée pour rattraper les `level: 'Junior'` (string) hérités.
 */
export function migrateDriversLevel(drivers) {
  if (!Array.isArray(drivers)) return drivers;
  let anyChange = false;
  const out = drivers.map(d => {
    if (!d || typeof d !== 'object') return d;
    const normalized = normalizeDriverLevel(d.level);
    if (normalized !== d.level) {
      anyChange = true;
      return { ...d, level: normalized };
    }
    return d;
  });
  return anyChange ? out : drivers;
}

/**
 * Label tri-tier visible (Junior / Confirmé / Sénior) calculé à partir
 * du niveau numérique 1-5. Permet d'afficher le terme demandé par
 * l'utilisateur tout en gardant la granularité 1-5 du système interne.
 */
export function getSeniorityLabel(level) {
  const n = normalizeDriverLevel(level);
  if (n <= 2) return 'Junior';
  if (n === 3) return 'Confirmé';
  return 'Sénior';
}

/**
 * Petit explainer pédagogique des paliers de carrière (affiché en tooltip
 * dans le dashboard RH suite au retour user qui ne savait pas la diff).
 */
export const SENIORITY_TIERS = [
  { label: 'Junior', range: '1-2', desc: 'Débute son parcours. Apprentissage rapide, bonus modestes.' },
  { label: 'Confirmé', range: '3', desc: 'Polyvalent, autonome sur la majorité des contrats.' },
  { label: 'Sénior', range: '4-5', desc: 'Expert reconnu : bonus revenu/carburant maximaux, peut former les juniors.' },
];
